import { useState } from 'react'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Popover from '@mui/material/Popover'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'

// Preset windows are "live": start is recomputed at every fetch and end
// is left open so the server pins it to now. Custom (and chart-zoom)
// ranges carry fixed start/end epochs instead.
export const PRESETS = [
  { key: 'today', label: 'Today', start: () => dayjs().startOf('day') },
  { key: '1h', label: '1h', start: () => dayjs().subtract(1, 'hour') },
  { key: '6h', label: '6h', start: () => dayjs().subtract(6, 'hour') },
  { key: '24h', label: '24h', start: () => dayjs().subtract(24, 'hour') },
  { key: '7d', label: '7d', start: () => dayjs().subtract(7, 'day') },
  { key: '30d', label: '30d', start: () => dayjs().subtract(30, 'day') },
]

export const resolveRange = (range) => {
  const preset = PRESETS.find(({ key }) => key === range.key)
  if (preset) return { start: preset.start().unix(), end: null }
  return { start: range.start, end: range.end }
}

const RangePicker = ({ range, onChange }) => {
  const [anchor, setAnchor] = useState(null)
  const [customStart, setCustomStart] = useState(() =>
    dayjs().subtract(1, 'day')
  )
  const [customEnd, setCustomEnd] = useState(() => dayjs())

  const isCustom = range.key === 'custom'

  const openCustom = (event) => {
    if (isCustom) {
      setCustomStart(dayjs.unix(range.start))
      setCustomEnd(dayjs.unix(range.end))
    }
    setAnchor(event.currentTarget)
  }

  const applyCustom = () => {
    onChange({
      key: 'custom',
      start: customStart.unix(),
      end: customEnd.unix(),
    })
    setAnchor(null)
  }

  const customInvalid =
    !customStart ||
    !customEnd ||
    !customStart.isValid() ||
    !customEnd.isValid() ||
    !customEnd.isAfter(customStart)

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <ToggleButtonGroup
        value={range.key}
        exclusive
        size="small"
        onChange={(e, key) => key && key !== 'custom' && onChange({ key })}
      >
        {PRESETS.map(({ key, label }) => (
          <ToggleButton key={key} value={key}>
            {label}
          </ToggleButton>
        ))}
        <ToggleButton value="custom" onClick={openCustom}>
          {isCustom
            ? `${dayjs.unix(range.start).format('MMM D HH:mm')} – ${dayjs
                .unix(range.end)
                .format('MMM D HH:mm')}`
            : 'Custom'}
        </ToggleButton>
      </ToggleButtonGroup>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: 280,
            }}
          >
            <Typography variant="subtitle2">Custom range</Typography>
            <DateTimePicker
              label="From"
              value={customStart}
              onChange={setCustomStart}
              disableFuture
              slotProps={{ textField: { size: 'small' } }}
            />
            <DateTimePicker
              label="To"
              value={customEnd}
              onChange={setCustomEnd}
              disableFuture
              slotProps={{ textField: { size: 'small' } }}
            />
            <Button
              variant="contained"
              size="small"
              disabled={customInvalid}
              onClick={applyCustom}
            >
              Apply
            </Button>
          </Box>
        </LocalizationProvider>
      </Popover>
    </Box>
  )
}

export default RangePicker
